import React, { useRef, useEffect, useState } from 'react';
import {
  select,
  scalePoint,
  scaleLinear,
  max,
  axisLeft,
  axisBottom,
  area as d3Area,
  curveMonotoneX,
} from 'd3';

function VehicleTrackingStationOverview({ data = [] }) {
  const svgRef = useRef();
  const wrapperRef = useRef();
  const [dimensions, setDimensions] = useState({ width: 0, height: 380 });

  // Tooltip state: type is 'incoming' | 'current' | 'delayed'
  const [tooltip, setTooltip] = useState({
    visible: false,
    type: null,
    x: 0,
    y: 0,
    d: null,
  });

  useEffect(() => {
    if (!wrapperRef.current) return undefined;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const { width } = entry.contentRect;

      const isMobile = width < 480;
      setDimensions({
        width,
        height: isMobile ? 280 : 380,
      });
    });

    observer.observe(wrapperRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const { width, height } = dimensions;
    if (width === 0) return;

    const svg = select(svgRef.current)
      .attr('width', '100%')
      .attr('height', height)
      .attr('viewBox', `0 0 ${width} ${height}`);

    svg.selectAll('*').remove();

    const isMobile = width < 480;

    const margin = {
      top: isMobile ? 30 : 40,
      right: isMobile ? 10 : 20,
      bottom: isMobile ? 30 : 40,
      left: isMobile ? 10 : 20,
    };

    const x = scalePoint()
      .domain(data.map((d) => d.station))
      .range([margin.left, width - margin.right])
      .padding(isMobile ? 0.4 : 0.6);

    const y = scaleLinear()
      .domain([
        0,
        max(data, (d) =>
          Math.max(d.incoming || 0, d.current || 0, d.delayed || 0),
        ),
      ])
      .nice()
      .range([height - margin.bottom, margin.top]);

    // Grid
    svg
      .append('g')
      .attr('class', 'y-grid')
      .attr('transform', `translate(${margin.left},0)`)
      .call(
        axisLeft(y)
          .ticks(isMobile ? 3 : 5)
          .tickSize(-(width - margin.left - margin.right))
          .tickFormat(''),
      )
      .call((g) => g.select('.domain').remove())
      .call((g) => g.selectAll('.tick line').attr('stroke', '#dfdfdf'));

    // Area generators
    const areaIncoming = d3Area()
      .x((d) => x(d.station))
      .y0(y(0))
      .y1((d) => y(d.incoming))
      .curve(curveMonotoneX);

    const areaCurrent = d3Area()
      .x((d) => x(d.station))
      .y0(y(0))
      .y1((d) => y(d.current))
      .curve(curveMonotoneX);

    const areaDelayed = d3Area()
      .x((d) => x(d.station))
      .y0(y(0))
      .y1((d) => y(d.delayed))
      .curve(curveMonotoneX);

    svg
      .append('path')
      .datum(data)
      .attr('fill', '#D9B20E4a')
      .attr('fill-opacity', 0.3)
      .attr('stroke', '#D8BC37')
      .attr('stroke-width', 1)
      .attr('d', areaIncoming);

    svg
      .append('path')
      .datum(data)
      .attr('fill', '#6e9c3a4a')
      .attr('fill-opacity', 0.3)
      .attr('stroke', '#6E9C3A')
      .attr('stroke-width', 1)
      .attr('d', areaCurrent);

    svg
      .append('path')
      .datum(data)
      .attr('fill', '#E7332F1A')
      .attr('fill-opacity', 0.6)
      .attr('stroke', '#E7332F')
      .attr('stroke-width', 1)
      .attr('d', areaDelayed);

    const xAxis = svg
      .append('g')
      .attr('transform', `translate(0,${height - margin.bottom})`)
      .call(axisBottom(x));

    xAxis.select('.domain').attr('stroke', '#dfdfdf');

    if (isMobile) {
      xAxis
        .selectAll('text')
        .style('font-size', '10px')
        .attr('dy', '0.8em')
        .attr('transform', 'rotate(-45)')
        .style('text-anchor', 'end');
    } else {
      xAxis.selectAll('text').style('font-size', '12px');
    }

    svg
      .append('g')
      .attr('transform', `translate(${margin.left},0)`)
      .call(axisLeft(y))
      .call((g) => g.select('.domain').remove())
      .call((g) => g.selectAll('.tick').remove());

    const getTooltipCoords = (station, value) => ({
      x: x(station),
      y: y(value) - 40,
    });

    const showTooltip = (type, d) => {
      const value = type === 'incoming' ? d.incoming : d.current;
      const { x: tx, y: ty } = getTooltipCoords(d.station, value);
      setTooltip({
        visible: true,
        type,
        x: tx,
        y: ty,
        d,
      });
    };

    const hideTooltip = () =>
      setTooltip({ visible: false, type: null, x: 0, y: 0, d: null });

    svg
      .selectAll('.hover-circle-incoming')
      .data(data)
      .enter()
      .append('circle')
      .attr('class', 'hover-circle-incoming')
      .attr('cx', (d) => x(d.station))
      .attr('cy', (d) => y(d.incoming))
      .attr('r', isMobile ? 10 : 12)
      .attr('fill', 'transparent')
      .style('cursor', 'pointer')
      .on('mousemove', (event, d) => {
        showTooltip('incoming', d);
      })
      .on('mouseleave', hideTooltip)
      .on('touchstart', (event, d) => {
        event.preventDefault();
        showTooltip('incoming', d);
      });

    svg
      .selectAll('.hover-circle-current')
      .data(data)
      .enter()
      .append('circle')
      .attr('class', 'hover-circle-current')
      .attr('cx', (d) => x(d.station))
      .attr('cy', (d) => y(d.current))
      .attr('r', isMobile ? 10 : 12)
      .attr('fill', 'transparent')
      .style('cursor', 'pointer')
      .on('mousemove', (event, d) => {
        showTooltip('current', d);
      })
      .on('mouseleave', hideTooltip)
      .on('touchstart', (event, d) => {
        event.preventDefault();
        showTooltip('current', d);
      });

    // Hover circles for "Delayed"
    svg
      .selectAll('.hover-circle-delayed')
      .data(data)
      .enter()
      .append('circle')
      .attr('class', 'hover-circle-delayed')
      .attr('cx', (d) => x(d.station))
      .attr('cy', (d) => y(d.delayed))
      .attr('r', 12)
      .attr('fill', 'transparent')
      .style('cursor', 'pointer')
      .on('mousemove', (event, d) => {
        const bounds = wrapperRef.current.getBoundingClientRect();
        setTooltip({
          visible: true,
          type: 'delayed',
          x: x(d.station) + bounds.left - wrapperRef.current.offsetLeft,
          y: y(d.delayed) + bounds.top - wrapperRef.current.offsetTop - 40,
          d,
        });
      })
      .on('mouseleave', () =>
        setTooltip({ visible: false, type: null, x: 0, y: 0, d: null }),
      );

    // Show marker at point if tooltip
    if (tooltip.visible && tooltip.d) {
      svg
        .append('circle')
        .attr('cx', x(tooltip.d.station))
        .attr(
          'cy',
          tooltip.type === 'incoming'
            ? y(tooltip.d.incoming)
            : tooltip.type === 'current'
              ? y(tooltip.d.current)
              : y(tooltip.d.delayed),
        )
        .attr('r', 5)
        .attr(
          'fill',
          tooltip.type === 'incoming'
            ? '#D8BC37'
            : tooltip.type === 'current'
              ? '#3BA935'
              : '#E7332F',
        )
        .attr('stroke', '#fff')
        .attr('stroke-width', 2);
    }
  }, [data, dimensions, tooltip]);

  return (
    <div
      ref={wrapperRef}
      style={{
        width: '100%',
        position: 'relative',
        overflowX: 'auto',
      }}
    >
      <svg
        ref={svgRef}
        style={{
          width: '100%',
          height:
            dimensions.width < 480 ? dimensions.height + 80 : dimensions.height,
        }}
      />
      {tooltip.visible && tooltip.d && (
        <div
          style={{
            position: 'absolute',
            left: tooltip.x,
            top: tooltip.y,
            background: '#fff',
            border:
              tooltip.type === 'incoming'
                ? '1px solid #D8BC37'
                : tooltip.type === 'current'
                  ? '1px solid #6E9C3A'
                  : '1px solid #E7332F',
            padding: 8,
            borderRadius: 4,
            pointerEvents: 'none',
            fontSize: 12,
            boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
            minWidth: 90,
            zIndex: 1,
            transform: 'translate(-0%, -20%)',
          }}
        >
          <div className="div-flex-column rg-5">
            <div className="div-flex-row cg-5 div-align-center">
              <span
                style={{
                  color:
                    tooltip.type === 'incoming'
                      ? '#D8BC37'
                      : tooltip.type === 'current'
                        ? '#6E9C3A'
                        : '#E7332F',
                }}
                className="text-sm"
              >
                ●
              </span>
              <span className="text-md text-semi-bold">
                {tooltip.type === 'incoming'
                  ? 'Inward'
                  : tooltip.type === 'current'
                    ? 'Current'
                    : 'Delayed'}
              </span>
            </div>
            <div className="div-flex-row cg-5 div-align-center">
              <span className="text-md text-semi-bold">
                {tooltip.type === 'incoming'
                  ? String(tooltip.d.incoming).padStart(2, '0')
                  : tooltip.type === 'current'
                    ? String(tooltip.d.current).padStart(2, '0')
                    : String(tooltip.d.delayed).padStart(2, '0')}
              </span>
              <span className="text-md text-semi-bold">Vehicle</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default VehicleTrackingStationOverview;
